import React, { useState } from 'react';
import { MoreMenuButton, MoreMenuPopup } from '@/components/common/elements/MoreMenu';
import { DotsIcon } from '@/components/common/comment/commentIcons';
import { api } from '@/api/repository/axios';
import { useResultModal } from '@/hooks/useResultModal';
import ConfirmModal from '@/components/common/elements/ConfirmModal';
import ReportReasonSheet from './ReportReasonSheet';

/** 신고 대상 유형 — 백엔드 ReportTargetType 과 1:1. */
export type ReportTargetType =
    | 'POST'
    | 'POST_COMMENT'
    | 'POST_REPLY'
    | 'EXPERT_CONTENT'
    | 'EXPERT_COMMENT'
    | 'EXPERT_REPLY'
    | 'SEMINAR_REVIEW'
    | 'USER'
    | 'THREAD'
    | 'THREAD_MESSAGE';

/** 신고 사유 목록은 reportReasons.ts 로 옮겼다(공용 신고 시트와 함께 쓴다) — 기존 import 경로를 위해 다시 내보낸다 */
export { REPORT_REASONS } from './reportReasons';

interface ReportBlockMenuProps {
    /** 대상 유형 */
    targetType: ReportTargetType;
    /** 대상 ID (게시글/댓글/답글/콘텐츠/후기 id) — 작성자는 서버가 이 id 로 찾는다(화면은 작성자 회원 번호를 받지 않는다) */
    targetId: string;
    /** 댓글/답글이면 소속 게시글·콘텐츠 id (원문 추적용) */
    parentId?: string;
    /** 대상 표시명 (차단 확인 문구용) */
    authorNickname?: string;
    /** 차단 완료 후 콜백 (목록/상세 새로고침 등) */
    onBlocked?: () => void;
    /** (예전 케밥 아이콘 크기 — 공용 ⋯ 단추로 바뀌어 쓰지 않는다) */
    size?: 'small' | 'medium';
    /** 메뉴를 여는 단추를 바꿔 끼운다(댓글·답글은 공용 ⋯ 단추) — 없으면 기본 세로 점 단추 */
    renderTrigger?: (open: (event: React.MouseEvent<HTMLElement>) => void) => React.ReactNode;
}

/**
 * 남의 글·댓글·답글에 붙는 신고/차단 케밥 메뉴 (App Store Guideline 1.2 대응).
 * 신고는 사유 선택 다이얼로그, 차단은 확인 후 즉시 처리한다.
 */
const ReportBlockMenu: React.FC<ReportBlockMenuProps> = ({
    targetType,
    targetId,
    parentId,
    authorNickname,
    onBlocked,
    renderTrigger,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [reportOpen, setReportOpen] = useState(false);
    const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
    const { showSuccess, showError, resultModal } = useResultModal();

    const menuOpen = Boolean(anchorEl);

    const closeMenu = () => setAnchorEl(null);

    const openReport = () => {
        closeMenu();
        setReportOpen(true);
    };

    const openBlockConfirm = () => {
        closeMenu();
        setBlockConfirmOpen(true);
    };

    /** 신고 보내기 — 성공하면 true(시트가 닫힌다). 사유 검사·"기타" 글은 공용 시트가 맡는다 */
    const submitReport = async (reason: string, detail?: string): Promise<boolean> => {
        try {
            await api.moderation.report({ targetType, targetId, parentId, reason, detail });
            showSuccess('신고가 접수되었습니다. 검토 후 조치하겠습니다.');
            return true;
        } catch (e) {
            showError(e, '신고 실패');
            return false;
        }
    };

    const submitBlock = async () => {
        try {
            await api.moderation.blockByTarget({ targetType, targetId, parentId });
            showSuccess('차단했습니다. 이 사용자의 글과 댓글이 더 이상 보이지 않습니다.', '차단 완료', onBlocked);
        } catch (e) {
            showError(e, '차단 실패');
        }
    };

    return (
        <>
            {/* 단추·팝업은 글·댓글 공용 ⋯ 메뉴 모양 하나(D3) */}
            {renderTrigger ? (
                renderTrigger((e) => setAnchorEl(e.currentTarget))
            ) : (
                <MoreMenuButton type="button" aria-label="신고 또는 차단" aria-haspopup="menu" onClick={(e) => setAnchorEl(e.currentTarget)}>
                    <DotsIcon />
                </MoreMenuButton>
            )}

            <MoreMenuPopup
                anchorEl={anchorEl}
                onClose={closeMenu}
                items={[
                    { label: '신고하기', onClick: openReport, danger: true },
                    { label: '이 사용자 차단', onClick: openBlockConfirm },
                ]}
            />

            <ReportReasonSheet
                open={reportOpen}
                description="신고 사유를 선택하세요. 검토 후 24시간 안에 조치해요."
                onClose={() => setReportOpen(false)}
                onSubmit={submitReport}
            />

            <ConfirmModal
                open={blockConfirmOpen}
                title={`${authorNickname ? `'${authorNickname}'님을 ` : ''}차단할까요?`}
                description={'차단하면 이 사용자의 글과 댓글이 더 이상 보이지 않습니다. 차단은 마이페이지에서 해제할 수 있습니다.'}
                confirmText="차단"
                cancelText="취소"
                onClose={() => setBlockConfirmOpen(false)}
                onConfirm={submitBlock}
            />

            {resultModal}
        </>
    );
};

export default ReportBlockMenu;
